import { CopyButton } from '@nyce/admin-kit/console';
import { Chip } from '@nyce/admin-kit/ui';
import type { LogEntry } from '../../api/logs';
import { severityGroup } from '../../lib/logFilters';

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** Local time with milliseconds; the date is added when it is not today. */
export function formatLogTime(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
  const sameDay =
    d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  return sameDay ? time : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${time}`;
}

export function SeverityChip({
  entry,
}: {
  entry: Pick<LogEntry, 'severity' | 'gcpSeverity' | 'severitySource'>;
}) {
  const reclassified = entry.severitySource !== 'gcp';
  return (
    <span
      title={reclassified ? `Reclassified from ${entry.gcpSeverity} (${entry.severitySource})` : undefined}
    >
      <Chip size="sm" className={`oph-sev oph-sev-${severityGroup(entry.severity)}`}>
        {entry.severity}
        {reclassified ? '*' : ''}
      </Chip>
    </span>
  );
}

function prettyJson(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

/** One entry, opened under its row in the log table: metadata, full message and the payload JSON, each copyable. */
export function LogEntryDetail({
  entry,
  onCopied,
}: {
  entry: LogEntry;
  onCopied: (message: string) => void;
}) {
  const payload = prettyJson(entry.payload);
  return (
    <div className="oph-log-detail">
      <table className="nyk-ui-table nyk-kv">
        <tbody>
          <tr>
            <th>Time</th>
            <td className="nyk-mono">
              {formatLogTime(entry.timestamp)} <span className="nyk-ui-hint">({entry.timestamp})</span>
            </td>
          </tr>
          <tr>
            <th>Severity</th>
            <td>
              <SeverityChip entry={entry} />
              {entry.severitySource !== 'gcp' && (
                <span className="oph-log-reclass" data-testid="reclassified">
                  reclassified from {entry.gcpSeverity} ({entry.severitySource})
                </span>
              )}
            </td>
          </tr>
          <tr>
            <th>Namespace</th>
            <td>{entry.namespace}</td>
          </tr>
          <tr>
            <th>Pod</th>
            <td className="nyk-mono">{entry.pod}</td>
          </tr>
          <tr>
            <th>Container</th>
            <td>{entry.container}</td>
          </tr>
          <tr>
            <th>Log</th>
            <td className="nyk-mono nyk-wrap">{entry.logName}</td>
          </tr>
          <tr>
            <th>Insert id</th>
            <td className="nyk-mono">{entry.insertId}</td>
          </tr>
        </tbody>
      </table>
      <div className="oph-log-section">
        <div className="oph-log-section-head">
          <h3>Message</h3>
          <CopyButton
            text={entry.message}
            icon
            label="Copy message"
            onCopied={() => onCopied('Message copied')}
          />
        </div>
        <pre className="oph-json nyk-pre-wrap">{entry.message}</pre>
      </div>
      <div className="oph-log-section">
        <div className="oph-log-section-head">
          <h3>Payload</h3>
          <CopyButton text={payload} icon label="Copy payload" onCopied={() => onCopied('Payload copied')} />
        </div>
        <pre className="oph-json" data-testid="payload">
          {payload}
        </pre>
      </div>
    </div>
  );
}
